/**
 * Global-timeline math for the Premiere-style seekbar. Steps play back-to-back,
 * so the slide has one continuous timeline: step i occupies
 * `[start_i, start_i + dur_i)` global ms. These helpers convert between a global
 * playhead time and the (step, local-ms) it points at, and are the single source
 * of truth for both the seekbar geometry and the playback driver.
 */

import type { RevealStep } from '@/lib/schema.types';

import { stepDurationMs } from './clips';

export interface StepSpan {
  start: number; // global ms offset of this step
  dur: number; // step duration ms
}

export interface Timeline {
  spans: StepSpan[];
  total: number; // total slide duration ms
}

export function buildTimeline(steps: RevealStep[]): Timeline {
  let acc = 0;
  const spans = steps.map((s) => {
    const dur = stepDurationMs(s);
    const span = { start: acc, dur };
    acc += dur;
    return span;
  });
  return { spans, total: acc };
}

/** Map a global ms time to the step index it falls in + the local ms within it. */
export function globalToStep(
  spans: StepSpan[],
  globalMs: number,
): { index: number; localMs: number } {
  if (spans.length === 0) return { index: 0, localMs: 0 };
  const t = Math.max(0, globalMs);
  for (let i = 0; i < spans.length; i++) {
    const s = spans[i];
    if (t < s.start + s.dur || i === spans.length - 1) {
      return { index: i, localMs: Math.max(0, Math.min(s.dur, t - s.start)) };
    }
  }
  const last = spans[spans.length - 1];
  return { index: spans.length - 1, localMs: last.dur };
}

export function clampMs(ms: number, total: number): number {
  return Math.max(0, Math.min(total, ms));
}

/** "m:ss.t" — compact time display for the seekbar. */
export function fmtMs(ms: number): string {
  const s = Math.max(0, ms) / 1000;
  const m = Math.floor(s / 60);
  const rem = s - m * 60;
  return `${m}:${rem.toFixed(1).padStart(4, '0')}`;
}
